import { FC, useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import HelpTooltip from '@/components/ui/help-tooltip';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { PayloadSchemaField } from '@/services/data-sources/get-collection-info';

export type Cardinality = 'single' | 'multi';

interface ExposedUploadAttributesSectionProps {
  payloadSchema?: Record<string, PayloadSchemaField>;
  selected: string[];
  onChange: (next: string[]) => void;
  cardinality?: Record<string, Cardinality>;
  onCardinalityChange?: (next: Record<string, Cardinality>) => void;
  isLoading?: boolean;
  isError?: boolean;
  disabled?: boolean;
}

const humanizeDataType = (field: PayloadSchemaField): string => {
  if (!field?.data_type) return 'unknown';
  return field.data_type;
};

const isTextualType = (dataType: string | undefined): boolean => {
  return dataType === 'keyword' || dataType === 'text';
};

export const ExposedUploadAttributesSection: FC<
  ExposedUploadAttributesSectionProps
> = ({
  payloadSchema,
  selected,
  onChange,
  cardinality,
  onCardinalityChange,
  isLoading,
  isError,
  disabled,
}) => {
  const fields = useMemo(() => {
    if (!payloadSchema) return [];
    return Object.entries(payloadSchema)
      .map(([name, schema]) => ({ name, schema }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [payloadSchema]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const toggleField = (name: string, checked: boolean) => {
    if (checked) {
      if (selectedSet.has(name)) return;
      onChange([...selected, name]);
    } else {
      onChange(selected.filter((v) => v !== name));
      if (cardinality && onCardinalityChange && name in cardinality) {
        const next = { ...cardinality };
        delete next[name];
        onCardinalityChange(next);
      }
    }
  };

  const setFieldCardinality = (name: string, value: Cardinality) => {
    if (!onCardinalityChange) return;
    onCardinalityChange({ ...(cardinality ?? {}), [name]: value });
  };

  return (
    <div className='space-y-3'>
      <div className='flex items-center gap-2'>
        <Label className='text-sm font-medium'>Exposed Upload Attributes</Label>
        <HelpTooltip label='Exposed upload attributes help'>
          Select indexed fields end-users can set when uploading files. These
          map to Qdrant payload attributes and can be edited per-file after
          upload. For text fields, choose Single (one value) or Multiple (list
          of values).
        </HelpTooltip>
      </div>

      {isLoading && (
        <div className='space-y-2'>
          <Skeleton className='h-8 w-full' />
          <Skeleton className='h-8 w-full' />
          <Skeleton className='h-8 w-2/3' />
        </div>
      )}

      {!isLoading && isError && (
        <p className='text-sm text-muted-foreground'>
          Unable to load index fields. Try reopening the modal.
        </p>
      )}

      {!isLoading && !isError && fields.length === 0 && (
        <p className='text-sm text-muted-foreground'>
          This collection has no indexed payload fields yet.
        </p>
      )}

      {!isLoading && !isError && fields.length > 0 && (
        <div className='rounded-md border divide-y max-h-64 overflow-y-auto'>
          {fields.map(({ name, schema }) => {
            const id = `exposed-attr-${name}`;
            const isChecked = selectedSet.has(name);
            const showCardinalityToggle =
              isChecked &&
              isTextualType(schema?.data_type) &&
              !!onCardinalityChange;
            const currentCardinality: Cardinality =
              cardinality?.[name] ?? 'single';

            return (
              <div
                key={name}
                className='flex items-center gap-3 px-3 py-2 hover:bg-muted/50'
              >
                <label
                  htmlFor={id}
                  className='flex items-center gap-3 flex-1 min-w-0 cursor-pointer'
                >
                  <Checkbox
                    id={id}
                    checked={isChecked}
                    disabled={disabled}
                    onCheckedChange={(next) => toggleField(name, next === true)}
                  />
                  <span className='flex-1 text-sm font-medium truncate'>
                    {name}
                  </span>
                  <Badge
                    variant='outline'
                    className='shrink-0 font-mono text-xs'
                  >
                    {humanizeDataType(schema)}
                  </Badge>
                </label>
                {showCardinalityToggle && (
                  <ToggleGroup
                    type='single'
                    size='sm'
                    value={currentCardinality}
                    onValueChange={(value) => {
                      if (value === 'single' || value === 'multi') {
                        setFieldCardinality(name, value);
                      }
                    }}
                    disabled={disabled}
                    aria-label={`Cardinality for ${name}`}
                    className='shrink-0'
                  >
                    <ToggleGroupItem
                      value='single'
                      className='text-xs h-7 px-2'
                    >
                      Single
                    </ToggleGroupItem>
                    <ToggleGroupItem value='multi' className='text-xs h-7 px-2'>
                      Multiple
                    </ToggleGroupItem>
                  </ToggleGroup>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
